import React from 'react'

import {noop} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import * as Yup from 'yup'

import {useAppDispatch} from '../../../hooks/redux'
import {fatalError} from '../../../redux/features/core'
import {
  closeModal,
  openAddDynamicProductionTimeModal,
  openCreateDynamicProductionTimeModal,
} from '../../../redux/features/modal'

import useAsync from '../../../../../commons/src/hooks/use-async'
import useKeyPress from '../../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as dynamicProductionTimeApi from '../../../services/api/dynamic-production-time'
import * as providerInfoApi from '../../../services/api/provider-info'
import {toTouchedFormData, toValidationSchema} from '../../utils/form'

import {
  DynamicProductionTime,
  DynamicProductionTimeId,
  ModalMeta,
  ProviderId,
  ProviderInfo,
} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import Link from '../../../../../commons/src/components/link'
import Modal from '../../../../../commons/src/components/modal'
import Paragraph from '../../../../../commons/src/components/paragraph'
import SelectField from '../../../../../commons/src/components/select-field'
import SelectMenu from '../../../../../commons/src/components/select-menu'

import {
  DynamicProductionTimeOption,
  SingleFormData,
} from '../../provider-info/edit-dynamic-production-time-partial'

type Props = {
  providerId: ProviderId
  providerInfos: ProviderInfo[]
  initialDynamicProductionTimeId: DynamicProductionTimeId
  meta: ModalMeta
  onDone?: () => void
}

export const initialValues = {
  dynamicProductionTimeId: '',
}

export const validationSchema = {
  dynamicProductionTimeId: Yup.string().label('Dynamic production time').required(),
}

const AddDynamicProductionTimeModal = ({
  meta,
  onDone = noop,
  providerId,
  providerInfos,
  initialDynamicProductionTimeId,
}: Props) => {
  const dispatch = useAppDispatch()

  const {isLoading, data: dynamicProductionTimes = []} = useAsync(
    () => dynamicProductionTimeApi.getByProvider(providerId),
    [],
    {
      onError: error => dispatch(fatalError({error})),
    }
  )

  const formik = useFormik<SingleFormData>({
    enableReinitialize: true,
    initialValues,
    validationSchema: toValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const providerInfoRequest = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>

      try {
        const providerInfoIds = providerInfos.map(info => info.id)
        await providerInfoApi.patchMultiple(providerInfoRequest, providerInfoIds)

        onDone()
        dispatch(closeModal())
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  React.useEffect(() => {
    if (initialDynamicProductionTimeId) {
      formik.setFieldValue('dynamicProductionTimeId', initialDynamicProductionTimeId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDynamicProductionTimeId])

  const setCreatedDynamicProductionTime = (dynamicProductionTime: DynamicProductionTime) => {
    // Reopen self with initial dynamicProductionTimeId onDone
    dispatch(
      openAddDynamicProductionTimeModal({
        providerId,
        providerInfos,
        onDone,
        initialDynamicProductionTimeId: dynamicProductionTime.id,
      })
    )
  }

  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  const dynamicProductionTimeOptions: DynamicProductionTimeOption[] = dynamicProductionTimes.map(
    ({id, name}) => ({
      label: name,
      value: id,
    })
  )

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Add dynamic production time" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
        <Paragraph>
          You can either select one of your existing dynamic production time configuration or you
          can{' '}
          <Link
            onClick={() =>
              dispatch(
                openCreateDynamicProductionTimeModal({onDone: setCreatedDynamicProductionTime})
              )
            }
          >
            create a new one
          </Link>
          .
        </Paragraph>
        <FormRow>
          <FieldNote
            appearance="error"
            note={formik.submitCount > 0 && formik.errors.dynamicProductionTimeId}
          >
            <SelectField
              name="type"
              disabled={isLoading}
              placeholder={
                isLoading
                  ? 'Loading dynamic production time options...'
                  : 'Select dynamic production time'
              }
              value={dynamicProductionTimeOptions.find(
                ({value}) => value === formik.values.dynamicProductionTimeId
              )}
              menu={<SelectMenu values={dynamicProductionTimeOptions} />}
              onChange={option => {
                formik.setFieldValue(
                  'dynamicProductionTimeId',
                  (option as DynamicProductionTimeOption).value
                )
              }}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FormRow>
      </form>
    </Modal>
  )
}

export default AddDynamicProductionTimeModal
